import useFormatter from "@/hooks/useFormatter";
import useConfirm from "@/hooks/useConfirm";
import StatValue from "@/components/common/StatValue";
import { StatGridSkeleton } from "@/components/loading";
import { statsKeys, usersKeys } from "@/query-keys";
import { services } from "@/services";
import { createFileRoute, Link, Outlet, useNavigate, useMatchRoute, redirect } from "@tanstack/react-router";
import {
  ArrowLeft,
  PhoneIcon,
  Mail,
  User as UserIcon,
  Calendar,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  Weight,
  Wrench,
  Wallet,
  Hash,
  RefreshCw,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import useUserQuery from "../_hooks/useUserQuery";
import useUserMutation from "../_hooks/useUserMutation";
import useUserStatsQuery from "../_hooks/useUserStatsQuery";

export const Route = createFileRoute("/_private/users/$userId")({
  beforeLoad: ({ context }) => {
    if (!context.auth.user?.isRoot) {
      throw redirect({ to: "/stats" });
    }
  },
  loader: async ({ params, context: { queryClient } }) => {
    const [user, stats] = await Promise.all([
      queryClient.ensureQueryData({
        queryKey: usersKeys.detail(params.userId),
        queryFn: () => services.getUser(params.userId),
      }),
      queryClient.ensureQueryData({
        queryKey: statsKeys.users(params.userId),
        queryFn: () => services.getUserStats(params.userId),
      }),
    ]);
    return { user, stats };
  },
  component: UserDetailLayout,
});

function UserDetailLayout() {
  const { t } = useTranslation();
  const formatter = useFormatter();
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { userId } = Route.useParams();
  const { user: initialUser, stats: initialStats } = Route.useLoaderData();
  const { data: user } = useUserQuery(userId, initialUser);
  const { data: stats, isFetching } = useUserStatsQuery(userId, initialStats);
  const { deleteUser, isDeleting, toggleActiveStatus, isToggling } = useUserMutation();

  const deleteConfirm = useConfirm({
    title: t("users.confirm.delete.title"),
    description: t("users.confirm.delete.description"),
    isPending: isDeleting,
  });

  const toggleConfirm = useConfirm({
    title: t("users.confirm.deactivate.title"),
    description: t("users.confirm.deactivate.description"),
    variant: "default",
    isPending: isToggling,
  });

  const handleDelete = () =>
    deleteConfirm.confirm(async () => {
      await deleteUser(userId);
      navigate({ to: "/users" });
    });

  const handleToggle = () =>
    toggleConfirm.confirm(() => {
      toggleActiveStatus(userId);
    });

  return (
    <div className="flex-1 flex flex-col gap-8 pb-8">
      <div className="flex flex-col gap-4">
        <Link
          to="/users"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("transactions.detail.back")}
        </Link>
        <div className="space-y-4 sm:space-y-2">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <UserIcon className="h-5 w-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-xl font-bold tracking-tight capitalize">
                    {user.name ?? t("common.notAvailable")}
                  </h1>
                  <Badge variant={user.isActive ? "success" : "neutral"}>
                    {user.isActive ? t("common.active") : t("common.inactive")}
                  </Badge>
                  {user.deletedAt && (
                    <Badge variant="destructive">
                      <Trash2 className="h-3 w-3" />
                      {t("common.deleted")}
                    </Badge>
                  )}
                </div>
                {(user.phone || user.email) && (
                  <div className="flex items-center gap-3 mt-0.5 text-sm text-muted-foreground">
                    {user.phone && (
                      <span className="flex items-center gap-1.5" dir="ltr">
                        <PhoneIcon className="h-3.5 w-3.5" />
                        {formatter.phone(user.phone)}
                      </span>
                    )}
                    {user.email && (
                      <span className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" />
                        {user.email}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleToggle} disabled={!!user.deletedAt || user.isRoot}>
                {user.isActive ? (
                  <XCircle className="h-3.5 w-3.5" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                {user.isActive ? t("common.deactivate") : t("common.activate")}
              </Button>
              <Button variant="destructive" size="sm" onClick={handleDelete} disabled={!!user.deletedAt || user.isRoot}>
                <Trash2 className="h-3.5 w-3.5" />
                {t("common.delete")}
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {user.createdByName && (
              <span className="flex items-center gap-1.5">
                <UserIcon className="h-3.5 w-3.5" />
                {user.createdByName}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {formatter.dateTime(user.createdAt)}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2 h-8">
          <UserIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <h2 className="text-xs font-mono uppercase tracking-wider text-muted-foreground font-medium">
            {t("users.detail.stats.title")}
          </h2>
          {isFetching && (
            <RefreshCw className="h-3 w-3 animate-spin text-muted-foreground" />
          )}
        </div>
        {!stats ? (
          <StatGridSkeleton cols={3} />
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <StatValue
            icon={<Hash className="h-4 w-4" />}
            label={t("users.detail.stats.totalTransactions")}
            sub={t("users.detail.stats.totalTransactionsHint")}
            value={stats ? formatter.number(stats.totalTransactions) : null}
          />
          <StatValue
            icon={<Weight className="h-4 w-4" />}
            label={t("users.detail.stats.totalWeightHandled")}
            sub={t("users.detail.stats.totalWeightHandledHint")}
            value={stats ? formatter.weight(stats.totalWeightHandled, 3) : null}
          />
          <StatValue
            icon={<Clock className="h-4 w-4" />}
            label={t("users.detail.stats.lastTransactionAt")}
            sub={t("users.detail.stats.lastTransactionAtHint")}
            value={
              stats?.lastTransactionAt ? formatter.relativeTime(stats.lastTransactionAt) : null
            }
          />
          <StatValue
            icon={<Wrench className="h-4 w-4" />}
            label={t("users.detail.stats.totalLaborBilled")}
            sub={t("users.detail.stats.totalLaborBilledHint")}
            value={stats ? formatter.currency(stats.totalLaborBilled) : null}
          />
          <StatValue
            icon={<Wallet className="h-4 w-4" />}
            label={t("users.detail.stats.totalDepositCollected")}
            sub={t("users.detail.stats.totalDepositCollectedHint")}
            value={stats ? formatter.currency(stats.totalDepositCollected) : null}
          />
        </div>
        )}
      </div>

      <nav className="flex gap-0.5 rounded-lg bg-muted p-1 w-fit">
        <Link
          to="/users/$userId"
          params={{ userId }}
          className={`inline-flex items-center justify-center rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
            matchRoute({ to: "/users/$userId" })
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("users.detail.transactions")}
        </Link>
        <Link
          to="/users/$userId/charges"
          params={{ userId }}
          className={`inline-flex items-center justify-center rounded-md px-4 py-1.5 text-sm font-medium transition-all ${
            matchRoute({ to: "/users/$userId/charges" })
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("users.detail.charges")}
        </Link>
      </nav>

      <Outlet />

      {deleteConfirm.confirmDialog}
      {toggleConfirm.confirmDialog}
    </div>
  );
}
